import { z } from 'zod';

/**
 * Type de vente Énergie tel que renvoyé par le call-center Active Contact
 * dans le `raw_label` de la qualification de VENTE (`ACCORDS ELEC` /
 * `ACCORDS GAZ` / `ACCORDS DUAL`). C'est le pendant "énergie" des pastilles
 * de ventes TMK fibre/mobile côté Telco — à ceci près que l'info arrive
 * DIRECTEMENT dans la qualif hopti (aucune réconciliation BigQuery n'est
 * nécessaire, contrairement au Telco qui va lire `zaf_tmk_live`). Matérialisé
 * sur `Lead.energySaleType` à l'ingestion (cf. `QualificationsSyncService`).
 *
 *  - `ELEC` : contrat électricité seul
 *  - `GAZ`  : contrat gaz seul
 *  - `DUAL` : contrat électricité + gaz
 */
export const ENERGY_SALE_TYPES = ['ELEC', 'GAZ', 'DUAL'] as const;
export type EnergySaleType = (typeof ENERGY_SALE_TYPES)[number];
export const EnergySaleTypeSchema = z.enum(ENERGY_SALE_TYPES);

/** Libellé FR affiché sur la pastille (liste + détail). */
export const ENERGY_SALE_TYPE_LABEL_FR: Record<EnergySaleType, string> = {
  ELEC: 'Élec',
  GAZ: 'Gaz',
  DUAL: 'Dual',
};

/**
 * Id du compte call-center Active Contact côté Hopti (`meta.ccAccountId`).
 * SEUL partenaire dont le vocabulaire `ACCORDS <TYPE>` code le type
 * d'énergie — on scope le parsing à ce compte pour ne pas mal interpréter
 * le `raw_label` d'un autre partenaire énergie qui contiendrait par
 * coïncidence "elec"/"gaz".
 */
export const ACTIVE_CONTACT_CC_ACCOUNT_ID = '861536';

/**
 * Extrait le type d'énergie d'un `raw_label` Active Contact. Une vente y est
 * codifiée `ACCORDS <TYPE>` (observé en prod : `ACCORDS ELEC`, `ACCORDS GAZ`,
 * `ACCORDS DUAL`). On EXIGE le préfixe `accord(s)` pour ne matcher QUE les
 * ventes — sinon un `HORS CIBLE Gaz et électricité inclus dans ses charges`
 * (une NON-vente) deviendrait à tort une vente Dual. Ordre de résolution :
 * mot-clé `dual` explicite, sinon présence simultanée élec+gaz, sinon l'un
 * ou l'autre. Renvoie `null` si le label n'est pas une vente énergie
 * catégorisable (le champ `Lead.energySaleType` reste alors inchangé).
 */
export function parseEnergySaleType(
  rawLabel: string | null | undefined,
): EnergySaleType | null {
  if (!rawLabel) return null;
  const s = rawLabel.toLowerCase();
  // Uniquement les libellés de vente ("accord" / "accords").
  if (!/\baccord/.test(s)) return null;
  if (/\bdual\b/.test(s)) return 'DUAL';
  // `[eé]lec` couvre "elec" (ACCORDS ELEC) ET "élec…" (électricité) ;
  // `gaz` couvre "gaz" / "GAZ".
  const hasElec = /[eé]lec/.test(s);
  const hasGaz = /gaz/.test(s);
  if (hasElec && hasGaz) return 'DUAL';
  if (hasElec) return 'ELEC';
  if (hasGaz) return 'GAZ';
  return null;
}
